import { BaseElement, FormAssociated, css, html, define, upgradeProperties, emitValueEvents } from '../../../core/index.js';

const TAG_NAME = 'omni-toggle';

const styles = css`
:host {
  --iam-toggle-width: var(--toggle-width, 2.75rem);
  --iam-toggle-height: var(--toggle-height, 1.5rem);
  --iam-toggle-thumb-size: var(--toggle-thumb-size, 1.25rem);
  --iam-toggle-thumb-inset: var(--toggle-thumb-inset, 0.125rem);
  --iam-toggle-track-off: var(--toggle-track-off, rgba(255, 255, 255, 0.2));
  --iam-toggle-track-on: var(--toggle-track-on, var(--primary-color, #00C0FB));
  --iam-toggle-track-border: var(--toggle-track-border, rgba(0, 0, 0, 0.15));
  --iam-toggle-thumb-bg: var(--toggle-thumb-bg, #fff);
  --iam-toggle-thumb-border: var(--toggle-thumb-border, rgba(0, 0, 0, 0.1));
  --iam-toggle-focus-ring: var(--toggle-focus-ring, rgba(0, 192, 251, 0.35));
  display: inline-flex;
  align-items: center;
  gap: var(--toggle-gap, 0.35rem);
  cursor: pointer;
  vertical-align: middle;
  user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

:host([disabled]) {
  cursor: not-allowed;
  opacity: 0.55;
}

:host([hidden]) {
  display: none !important;
}

.control {
  position: relative;
  width: var(--iam-toggle-width);
  height: var(--iam-toggle-height);
  border-radius: var(--iam-toggle-height);
  background: var(--iam-toggle-track-off);
  border: 1px solid var(--iam-toggle-track-border);
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  box-sizing: border-box;
}

:host([checked]) .control {
  background: var(--iam-toggle-track-on);
  border-color: var(--iam-toggle-track-on);
}

:host(:focus-visible) .control {
  box-shadow: 0 0 0 3px var(--iam-toggle-focus-ring);
}

.thumb {
  position: absolute;
  top: 50%;
  left: var(--iam-toggle-thumb-inset);
  width: var(--iam-toggle-thumb-size);
  height: var(--iam-toggle-thumb-size);
  border-radius: 50%;
  background: var(--iam-toggle-thumb-bg);
  border: 1px solid var(--iam-toggle-thumb-border);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  transform: translate3d(0, -50%, 0);
  transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  will-change: transform;
  box-sizing: border-box;
}

:host([checked]) .thumb {
  transform: translate3d(calc(var(--iam-toggle-width) - var(--iam-toggle-thumb-size) - (var(--iam-toggle-thumb-inset) * 2)), -50%, 0);
}

:host(:active:not([disabled])) .thumb {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

slot:empty {
  display: none;
}
`;

const template = html`
  <div class="control" part="control">
    <div class="thumb" part="thumb" aria-hidden="true"></div>
  </div>
  <slot part="label"></slot>
`;

class OmniToggle extends FormAssociated(BaseElement) {
  static formAssociated = super.formAssociated;
  static shadowStyleSheets = [styles];
  static get observedAttributes() {
    return ['checked', 'disabled', 'name', 'value'];
  }

  constructor() {
    super();
    upgradeProperties(this, ['checked', 'disabled', 'name', 'value']);

    this._defaultChecked = this.hasAttribute('checked');
    this._supportsFACE = !!this._internals;
    this._hiddenInput = this._supportsFACE ? null : document.createElement('input');
    if (this._hiddenInput) {
      this._hiddenInput.type = 'hidden';
    }

    this._root.append(template.cloneNode(true));

    this.addEventListener('click', (event) => this._handleClick(event));
    this.addEventListener('keydown', (event) => this._handleKeyDown(event));
  }

  connectedCallback() {
    super.connectedCallback?.();
    if (this._defaultChecked === undefined) {
      this._defaultChecked = this.hasAttribute('checked');
    }

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'switch');
    }

    if (!this.hasAttribute('tabindex')) {
      this.tabIndex = this.disabled ? -1 : 0;
    }

    if (this._hiddenInput && !this._hiddenInput.isConnected) {
      const form = this._getForm();
      if (form) form.appendChild(this._hiddenInput);
      else this.after(this._hiddenInput);
    }

    this._syncFormValue();
    this._syncA11y();
    this._syncTabIndex();
  }

  disconnectedCallback() {
    if (this._hiddenInput?.isConnected) {
      this._hiddenInput.remove();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue === newValue) return;
    switch (name) {
      case 'checked':
        this._syncA11y();
        this._syncFormValue();
        break;
      case 'disabled':
        this._syncA11y();
        this._syncTabIndex();
        this._syncFormValue();
        break;
      case 'name':
      case 'value':
        this._syncFormValue();
        break;
      default:
        break;
    }
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(value) {
    if (value == null || value === '') this.removeAttribute('name');
    else this.setAttribute('name', String(value));
  }

  get value() {
    return this.getAttribute('value') ?? 'on';
  }

  set value(newValue) {
    if (newValue == null) this.removeAttribute('value');
    else this.setAttribute('value', String(newValue));
  }

  get checked() {
    return this.hasAttribute('checked');
  }

  set checked(v) {
    v ? this.setAttribute('checked', '') : this.removeAttribute('checked');
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  set disabled(v) {
    v ? this.setAttribute('disabled', '') : this.removeAttribute('disabled');
  }

  get type() {
    return 'checkbox';
  }

  formResetCallback() {
    this.checked = !!this._defaultChecked;
  }

  formStateRestoreCallback(state) {
    this.checked = !!state;
  }

  _getForm() {
    return this.form ?? this.closest('form') ?? null;
  }

  _handleClick(event) {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    if (event.defaultPrevented) return;
    this._toggle();
  }

  _handleKeyDown(event) {
    if (this.disabled) return;
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this._toggle();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this._toggle();
    }
  }

  _toggle() {
    this.checked = !this.checked;
    this._emitValueEvents();
  }

  _emitValueEvents() {
    emitValueEvents(this);
  }

  _syncA11y() {
    const checked = this.checked;
    const disabled = this.disabled;
    this.setAttribute('aria-checked', String(checked));
    this.setAttribute('aria-disabled', String(disabled));
  }

  _syncTabIndex() {
    this.tabIndex = this.disabled ? -1 : 0;
  }

  _syncFormValue() {
    if (this._internals) {
      this._updateFormValue(this.checked ? this.value : null);
      this._internals.ariaChecked = String(this.checked);
      this._internals.ariaDisabled = String(this.disabled);
    } else {
      this._syncHiddenInput();
    }
  }

  _syncHiddenInput() {
    if (!this._hiddenInput) return;
    if (this.checked && this.name && !this.disabled) {
      this._hiddenInput.name = this.name;
      this._hiddenInput.value = this.value;
      this._hiddenInput.disabled = false;
    } else {
      this._hiddenInput.name = '';
      this._hiddenInput.value = '';
      this._hiddenInput.disabled = true;
    }
  }
}

define(TAG_NAME, OmniToggle);

export { OmniToggle };
